import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faRefresh,
  faGraduationCap,
  faBookOpen,
  faAward,
  faCreditCard,
  faTrophy,
  faExclamationTriangle,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { streetlearnApi } from '../api';
import './analytics.css';

const StreetlearnAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  const [trends, setTrends] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [examStats, setExamStats] = useState([]);
  const [toughestLessons, setToughestLessons] = useState([]);
  const [questionHealth, setQuestionHealth] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // overview | funnel | exams | health

  const loadData = async () => {
    try {
      setLoading(true);
      const [ovRes, trRes, fnRes, exRes, tlRes, qhRes] = await Promise.allSettled([
        streetlearnApi.getAnalyticsOverview(),
        streetlearnApi.getAnalyticsActivityTrends(trendDays),
        streetlearnApi.getAnalyticsFunnel(),
        streetlearnApi.getAnalyticsExams(),
        streetlearnApi.getAnalyticsToughestLessons(10),
        streetlearnApi.getAnalyticsQuestionHealth(),
      ]);

      if (ovRes.status === 'fulfilled') setOverview(ovRes.value);
      if (trRes.status === 'fulfilled') setTrends(trRes.value || []);
      if (fnRes.status === 'fulfilled') setFunnel(fnRes.value || []);
      if (exRes.status === 'fulfilled') setExamStats(exRes.value || []);
      if (tlRes.status === 'fulfilled') setToughestLessons(tlRes.value || []);
      if (qhRes.status === 'fulfilled') setQuestionHealth(qhRes.value || []);
    } catch (err) {
      console.error('Failed to load StreetLearn analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [trendDays]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const maxActive = Math.max(...trends.map((t) => t.active_users || 0), 1);
  const maxRevenue = Math.max(...trends.map((t) => t.revenue || 0), 1);

  return (
    <div className="mt-5 streetlearn-analytics">
      {/* Page Header */}
      <div className="row pb-2 align-items-center">
        <div className="col-md-7">
          <h4 className="panel-title pull-left m-0">
            <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
            StreetLearn Analytics & Learning Funnel
          </h4>
        </div>
        <div className="col-md-5 text-right">
          <div className="btn-group me-2" role="group">
            <button
              type="button"
              className={`btn btn-xs ${trendDays === 7 ? 'btn-primary active' : 'btn-default'}`}
              onClick={() => setTrendDays(7)}
            >
              7 Days
            </button>
            <button
              type="button"
              className={`btn btn-xs ${trendDays === 14 ? 'btn-primary active' : 'btn-default'}`}
              onClick={() => setTrendDays(14)}
            >
              14 Days
            </button>
            <button
              type="button"
              className={`btn btn-xs ${trendDays === 30 ? 'btn-primary active' : 'btn-default'}`}
              onClick={() => setTrendDays(30)}
            >
              30 Days
            </button>
          </div>
          <button className="btn btn-default btn-sm" onClick={loadData} disabled={loading}>
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI Stats Cards (Exact LinkTrackingReport / CRM Standard Style) */}
      <div className="row mb-3">
        {/* Card 1: Total Learners (Dark Navy Blue) */}
        <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-dark-blue inverse-mode"
            style={{
              background: 'linear-gradient(135deg, #1b355a 0%, #28548a 100%)',
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Total Learners
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {Number(overview?.learners?.total || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* Card 2: Active Learners (Vibrant Light Blue) */}
        <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-blue inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Active (WAU)
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {Number(overview?.learners?.wau || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* Card 3: Total Revenue (Green) */}
        <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-green inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Total Revenue
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {formatCurrency(overview?.revenue?.total_revenue)}
            </div>
          </div>
        </div>

        {/* Card 4: Paid Orders (Red) */}
        <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-red inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Paid Orders
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {Number(overview?.revenue?.paid_orders || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* Card 5: Lessons Done (Purple) */}
        <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-purple inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Lessons Done
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {Number(overview?.progress?.completed_lessons || 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* Card 6: Exam Pass Rate (Orange) */}
        <div className="col-xl-2 col-lg-2 col-md-4 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-orange inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Exam Pass Rate
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {overview?.exams?.pass_rate_pct || 0}%
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation (Standard CRM button group) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body py-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'overview' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview & Activity Trends
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'funnel' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('funnel')}
            >
              16-Level Funnel Matrix
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'exams' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('exams')}
            >
              Exam Diagnostics
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'health' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('health')}
            >
              Question Pool Health
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================== */}
      {/* TAB 1: OVERVIEW & TRENDS */}
      {/* ============================================================== */}
      {activeTab === 'overview' && (
        <>
          <div className="row mb-3">
            {/* Chart 1: DAU */}
            <div className="col-md-6 mb-3">
              <div className="panel panel-default with-rounded-corner with-shadow h-100">
                <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
                  <h4 className="panel-title m-0">Daily Active Learners (DAU)</h4>
                  <span className="badge badge-info">Last {trendDays} Days</span>
                </div>
                <div className="panel-body">
                  <div className="sl-bar-chart">
                    {trends.map((t, idx) => {
                      const heightPct = Math.round(((t.active_users || 0) / maxActive) * 100);
                      return (
                        <div className="sl-bar-col" key={t.date || idx}>
                          <div className="sl-bar-tooltip">
                            <strong>{t.date}</strong>
                            <span>Active: {t.active_users}</span>
                            <span>Signups: {t.new_learners}</span>
                          </div>
                          <div
                            className="sl-bar-fill purple"
                            style={{ height: `${Math.max(heightPct, 4)}%` }}
                          />
                          {idx % Math.ceil(trends.length / 7) === 0 && (
                            <span className="sl-bar-label">{t.date.slice(5)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Chart 2: Daily Revenue */}
            <div className="col-md-6 mb-3">
              <div className="panel panel-default with-rounded-corner with-shadow h-100">
                <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
                  <h4 className="panel-title m-0">Revenue & Orders Trend</h4>
                  <span className="badge badge-success">Razorpay Verified</span>
                </div>
                <div className="panel-body">
                  <div className="sl-bar-chart">
                    {trends.map((t, idx) => {
                      const heightPct =
                        maxRevenue > 0
                          ? Math.round(((t.revenue || 0) / maxRevenue) * 100)
                          : 0;
                      return (
                        <div className="sl-bar-col" key={t.date || idx}>
                          <div className="sl-bar-tooltip">
                            <strong>{t.date}</strong>
                            <span>Revenue: {formatCurrency(t.revenue)}</span>
                            <span>Orders: {t.orders}</span>
                          </div>
                          <div
                            className="sl-bar-fill emerald"
                            style={{ height: `${Math.max(heightPct, 4)}%` }}
                          />
                          {idx % Math.ceil(trends.length / 7) === 0 && (
                            <span className="sl-bar-label">{t.date.slice(5)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Toughest Lessons Table */}
          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="panel-heading border-0">
              <h4 className="panel-title m-0">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                Toughest Lessons (Lowest Quiz Pass Rates)
              </h4>
            </div>
            <div className="panel-body p-0">
              {toughestLessons.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  No quiz attempt data recorded yet.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Lesson</th>
                        <th>Level</th>
                        <th>Attempts</th>
                        <th>Passed</th>
                        <th>Failed</th>
                        <th>Avg Score</th>
                        <th>Quiz Pass Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toughestLessons.map((l) => (
                        <tr key={l.lesson_id}>
                          <td>
                            <strong>{l.code}</strong> — {l.title}
                          </td>
                          <td>{l.level_title}</td>
                          <td>{l.total_attempts}</td>
                          <td className="text-success font-bold">{l.passed_count}</td>
                          <td className="text-danger">{l.failed_count}</td>
                          <td>{l.avg_score_pct}%</td>
                          <td>
                            <span
                              className={`badge ${
                                l.pass_rate_pct < 50
                                  ? 'badge-danger'
                                  : l.pass_rate_pct < 75
                                  ? 'badge-warning'
                                  : 'badge-success'
                              }`}
                            >
                              {l.pass_rate_pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ============================================================== */}
      {/* TAB 2: 16-LEVEL FUNNEL MATRIX */}
      {/* ============================================================== */}
      {activeTab === 'funnel' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">16-Level Curriculum Funnel & Retention</h4>
          </div>
          <div className="panel-body">
            <div className="sl-funnel-grid">
              {funnel.map((lvl) => (
                <div className="sl-funnel-card" key={lvl.level_id}>
                  <div className="sl-funnel-card-header">
                    <span className="badge badge-primary">Level {lvl.number}</span>
                    <span className="badge badge-default">{lvl.tier_name}</span>
                  </div>
                  <div className="sl-funnel-card-title">{lvl.title}</div>
                  <div className="text-muted small mb-2">Lessons: {lvl.lesson_count}</div>

                  <div className="sl-funnel-step">
                    <span className="text-muted">Started</span>
                    <span className="font-bold">{lvl.started_learners}</span>
                  </div>
                  <div className="sl-funnel-step">
                    <span className="text-muted">Exam Attempted</span>
                    <span className="font-bold">{lvl.exam_attempted_learners}</span>
                  </div>
                  <div className="sl-funnel-step">
                    <span className="text-muted">Exam Passed</span>
                    <span className="font-bold text-success">{lvl.exam_passed_learners}</span>
                  </div>

                  <div className="sl-funnel-footer">
                    <div>
                      Pass Rate:{' '}
                      <span
                        className={`badge ${
                          lvl.exam_pass_rate_pct >= 70 ? 'badge-success' : 'badge-warning'
                        }`}
                      >
                        {lvl.exam_pass_rate_pct}%
                      </span>
                    </div>
                    <div>
                      Drop-off:{' '}
                      <span className="text-danger font-bold">{lvl.drop_off_pct}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 3: EXAM PERFORMANCE MATRIX */}
      {/* ============================================================== */}
      {activeTab === 'exams' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">Level Certification Exam Diagnostics</h4>
          </div>
          <div className="panel-body p-0">
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Tier</th>
                    <th>Required Pass %</th>
                    <th>Exam Questions</th>
                    <th>Time Limit</th>
                    <th>Attempts</th>
                    <th>Passed</th>
                    <th>Failed</th>
                    <th>Avg Score</th>
                    <th>Pass Rate %</th>
                  </tr>
                </thead>
                <tbody>
                  {examStats.map((ex) => (
                    <tr key={ex.level_id}>
                      <td>
                        <strong>Level {ex.number}</strong>: {ex.title}
                      </td>
                      <td>
                        <span className="badge badge-info">{ex.tier_name}</span>
                      </td>
                      <td>{ex.required_pass_pct}%</td>
                      <td>{ex.question_count} Qs</td>
                      <td>{ex.time_limit_min} min</td>
                      <td>{ex.total_attempts}</td>
                      <td className="text-success font-bold">{ex.passed_attempts}</td>
                      <td className="text-danger">{ex.failed_attempts}</td>
                      <td>{ex.avg_score_pct}%</td>
                      <td>
                        <span
                          className={`badge ${
                            ex.total_attempts === 0
                              ? 'badge-default'
                              : ex.pass_rate_pct >= ex.required_pass_pct
                              ? 'badge-success'
                              : 'badge-danger'
                          }`}
                        >
                          {ex.total_attempts === 0 ? 'No attempts' : `${ex.pass_rate_pct}%`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* TAB 4: QUESTION POOL HEALTH */}
      {/* ============================================================== */}
      {activeTab === 'health' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">Question Bank Pool Health Matrix</h4>
          </div>
          <div className="panel-body p-0">
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Total Questions</th>
                    <th>Quiz Pool</th>
                    <th>Exam Pool</th>
                    <th>Exam Required</th>
                    <th>Published</th>
                    <th>Draft</th>
                    <th>Pool Health Status</th>
                  </tr>
                </thead>
                <tbody>
                  {questionHealth.map((q) => (
                    <tr key={q.level_id}>
                      <td>
                        <strong>Level {q.level_id}</strong>: {q.level_title}
                      </td>
                      <td>
                        <strong>{q.total_questions}</strong>
                      </td>
                      <td>{q.quiz_questions}</td>
                      <td>{q.exam_questions}</td>
                      <td>{q.exam_required_count}</td>
                      <td className="text-success">{q.published_count}</td>
                      <td className="text-warning">{q.draft_count}</td>
                      <td>
                        <span
                          className={`badge ${
                            q.pool_health === 'ok'
                              ? 'badge-success'
                              : q.pool_health === 'tight'
                              ? 'badge-warning'
                              : 'badge-danger'
                          }`}
                        >
                          {q.pool_health === 'ok' && 'Healthy (2x+)'}
                          {q.pool_health === 'tight' && 'Tight (<2x)'}
                          {q.pool_health === 'short' && 'Short (Deficit)'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreetlearnAnalytics;
